import { NavLink } from 'react-router-dom';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { useSupportUnreadCount } from '@/features/support/queries';
import { useItemLabel } from '@/hooks/useIndustry';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/stores/authStore';
import { useUiStore } from '@/stores/uiStore';
import { buildGroups, type NavItem } from './navigation';

export function Sidebar() {
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggle = useUiStore((s) => s.toggleSidebar);

  // Polled, because support is the one place the other party writes without
  // the user doing anything. The hook disables itself when they cannot see
  // support at all, rather than 403ing on a timer.
  const { data: support } = useSupportUnreadCount();
  const unread = support?.unread ?? 0;

  const session = useAuthStore((s) => s.session);
  const capabilities = session?.capabilities ?? [];
  const features = session?.features ?? {};
  const modules = session?.modules ?? {};
  const isOwner = session?.user.is_owner ?? false;
  const itemLabel = useItemLabel();

  const visible = (item: NavItem): boolean => {
    if (item.feature && features[item.feature] !== true) return false;
    // Before the owner short-circuit, like `feature`: a module the shop turned
    // off is not something the owner wants to see either.
    if (item.module && modules[item.module] !== true) return false;
    if (!item.requires) return true;
    if (isOwner) return true;
    return item.requires.some((c) => capabilities.includes(c));
  };

  const groups = buildGroups(itemLabel.plural)
    .map((g) => ({ ...g, items: g.items.filter(visible) }))
    .filter((g) => g.items.length > 0);

  return (
    <aside
      className={cn(
        // The one large dark surface. Focus is re-tinted to the bright logo
        // green here because the deep --color-brand ring, tuned for contrast
        // against a white page, all but vanishes on black — and this app is
        // driven by keyboard.
        'flex h-dvh shrink-0 flex-col bg-nav transition-[width] duration-150',
        '**:focus-visible:outline-logo',
        collapsed ? 'w-16' : 'w-64',
      )}
    >
      <div
        className={cn(
          'flex h-16 items-center border-b border-nav-border',
          collapsed ? 'justify-center px-2' : 'px-4',
        )}
      >
        <Logo
          wordmark={!collapsed}
          markClassName="h-8 w-8"
          wordmarkClassName="text-[15px] text-nav-ink"
        />
      </div>

      <nav className="scroll-slim-dark min-h-0 flex-1 overflow-y-auto px-3 py-4">
        {groups.map((group) => (
          <div key={group.label} className="mb-5 last:mb-0">
            {!collapsed && (
              <p className="mb-1.5 px-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-nav-ink-muted">
                {group.label}
              </p>
            )}
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    title={collapsed ? item.label : undefined}
                    className={({ isActive }) =>
                      cn(
                        'relative flex items-center gap-3 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-nav-hover text-logo'
                          : 'text-nav-ink-muted hover:bg-nav-hover hover:text-nav-ink',
                        collapsed && 'justify-center px-0',
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <span
                            className="absolute left-0 h-5 w-[3px] rounded-r-full bg-logo"
                            aria-hidden
                          />
                        )}
                        <item.icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.75} />
                        {!collapsed && <span className="truncate">{item.label}</span>}

                        {item.badge === 'support' && unread > 0 && (
                          <span
                            // Collapsed, the rail is 16 units wide and there is
                            // no room for a number — a dot on the icon still
                            // says "look here", which is the whole job.
                            className={cn(
                              'shrink-0 rounded-full bg-logo font-semibold text-nav',
                              collapsed
                                ? 'absolute right-2.5 top-1.5 h-2 w-2'
                                : 'ml-auto px-1.5 py-0.5 text-[11px] leading-none',
                            )}
                            aria-label={`${unread} unread ${unread === 1 ? 'reply' : 'replies'}`}
                          >
                            {!collapsed && unread}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <SystemStatus collapsed={collapsed} />

      <button
        type="button"
        onClick={toggle}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        className="flex h-11 items-center justify-center border-t border-nav-border text-nav-ink-muted transition-colors hover:bg-nav-hover hover:text-nav-ink"
      >
        {collapsed ? (
          <PanelLeftOpen className="h-[18px] w-[18px]" strokeWidth={1.75} />
        ) : (
          <PanelLeftClose className="h-[18px] w-[18px]" strokeWidth={1.75} />
        )}
      </button>
    </aside>
  );
}

/**
 * The footer status pill.
 *
 * Reads "Offline — sales paused" when the browser reports no connection.
 * The POS is online-only by design, so being honest about that the moment it
 * matters is better than letting a cashier discover it at checkout.
 */
function SystemStatus({ collapsed }: { collapsed: boolean }) {
  const online = typeof navigator === 'undefined' ? true : navigator.onLine;

  return (
    <div className={cn('border-t border-nav-border px-4 py-3', collapsed && 'px-0 text-center')}>
      <div className={cn('flex items-center gap-2', collapsed && 'justify-center')}>
        {/* The bright logo green, not --color-good: the deep status green is
            tuned for a white page and reads as near-black on this surface. */}
        <span
          className={cn('h-2 w-2 shrink-0 rounded-full', online ? 'bg-logo' : 'bg-critical')}
          aria-hidden
        />
        {!collapsed && (
          <div className="min-w-0">
            <p className="truncate text-xs font-medium text-nav-ink">
              {online ? 'All systems online' : 'Offline — sales paused'}
            </p>
            <p className="text-[10px] text-nav-ink-muted">v1.0.0</p>
          </div>
        )}
      </div>
    </div>
  );
}
